import styled from "@emotion/styled";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import routing from "routing";
import { TextField } from "src/components/Default/LekUI/TextField";
import useSearchParams from "src/hooks/useSearchParams";
import newApi from "src/newApi";
import * as Yup from "yup";

import { PrimaryButton } from "../../../Default/LekUI/Button";
import { TextWithLink } from "../../../Default/LekUI/Layout/TextWithLink";

function SignUpForm() {
	const { t } = useTranslation();
	const queryParams = useSearchParams();
	const history = useHistory();

	const {
		handleSubmit,
		handleChange: handleChangeFormik,
		values,
		errors,
		setErrors,
	} = useFormik({
		initialValues: {
			email: "",
			login: "",
			password: "",
			confirmPassword: "",
		},
		validateOnChange: false,
		onSubmit: async ({ email, login, password }) => {
			try {
				await newApi.signUpUser({
					email,
					username: login,
					password,
				});
				if (queryParams.promocode) {
					try {
						await newApi.activateUserPromocode(queryParams.promocode);
					} catch {
						/* Nothing */
					}
				}
				return history.push({
					pathname: generatePath(routing.paths.signIn),
					state: {
						email,
					},
				});
			} catch (err) {
				const { response } = err;

				if (response.status === 400) {
					if (response.data.username) {
						setErrors({ login: t("errors.usernameExists") });
					}

					if (response.data.email) {
						setErrors({ email: t("errors.emailExists") });
					}
				} else {
					setErrors({ email: t("errors.somethingWentWrong") });
				}
			}
		},
		validationSchema: Yup.object().shape({
			email: Yup.string()
				.email(t("errors.wrongEmailFormat"))
				.required(t("errors.emptyField")),
			login: Yup.string().required(t("errors.emptyField")),
			password: Yup.string()
				.min(8, t("errors.passwordToShort"))
				.required(t("errors.emptyField")),
			confirmPassword: Yup.string().oneOf(
				[Yup.ref("password"), null],
				t("errors.passwordsNotTheSame"),
			),
		}),
	});

	const handleChange = (...params) => {
		setErrors({});
		handleChangeFormik(...params);
	};

	return (
		<>
			<form onSubmit={handleSubmit} className="auth--form gap-24">
				<TextField
					className="w-100"
					required
					id="email"
					label={t("auth.form.email")}
					name="email"
					type="email"
					onChange={handleChange}
					autoComplete="email"
					value={values.email}
					error={errors.email}
				/>
				<TextField
					className="w-100"
					required
					id="login"
					label={t("auth.form.login")}
					name="login"
					type="text"
					onChange={handleChange}
					autoComplete="login"
					value={values.login}
					error={errors.login}
				/>
				<TextField
					className="w-100"
					required
					name="password"
					label={t("auth.form.password")}
					type="password"
					id="password"
					autoComplete="current-password"
					onChange={handleChange}
					value={values.password}
					error={errors.password}
				/>
				<TextField
					className="w-100"
					required
					name="confirmPassword"
					label={t("auth.form.passwordRepeat")}
					type="password"
					id="confirmPassword"
					autoComplete="current-password"
					onChange={handleChange}
					value={values.confirmPassword}
					error={errors.confirmPassword}
				/>
				<PrimaryButton className="w-100" type="submit">
					{t("auth.signUp")}
				</PrimaryButton>
			</form>
			<TextWithLink
				link={routing.paths.signIn}
				linkText={t("auth.signIn")}
				questionText={t("auth.signInQuestion")}
			/>
		</>
	);
}

export default styled(SignUpForm)`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;
